import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Store, Fuel, ShoppingBasket, Coffee, Building2, CheckCircle2, FileText, MessageSquare, ClipboardCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import SectionHeading from "../components/SectionHeading";
import Marquee from "../components/Marquee";
import { IMG, PACK_BOX } from "../lib/media";

const SUPPLY_TO = [
  { icon: Fuel, name: "Petrol stations & forecourts" },
  { icon: ShoppingBasket, name: "Convenience stores" },
  { icon: Store, name: "Independent retailers" },
  { icon: Building2, name: "Restaurants" },
  { icon: Coffee, name: "Cafés & canteens" },
  { icon: Store, name: "Local food businesses" },
  { icon: Building2, name: "Offices & workplaces" },
  { icon: Store, name: "Garden centres" },
  { icon: ShoppingBasket, name: "Other food retailers" },
];

const SUPPLY_WHAT = [
  { name: "Freshly prepared sandwiches & baguettes", img: IMG.sandwich },
  { name: "Breads & rolls", img: IMG.bread_artisan },
  { name: "Cakes & sweet goods", img: IMG.cake_slice },
  { name: "Savoury slices & sausage rolls", img: IMG.pastries_mixed },
  { name: "Samosas & onion bhajis", img: IMG.indian_street },
  { name: "Other grab-and-go items", img: PACK_BOX },
];

const REASONS = [
  "Competitive pricing and discount models",
  "Fresh, high-quality products customers re-buy and come back for",
  "Strong presentation that lifts your bakery and grab-and-go shelf",
  "Reliable local supply with daily deliveries across St Neots, Huntingdonshire & Cambridgeshire",
  "Flexible ordering — start small, scale as it sells",
  "Relationship-led service. A real human, not a portal",
  "Sale-or-return approach with agreed returns and clean weekly invoicing",
  "Online marketing & social media support to raise awareness of SugaSpice in your store",
];

const PROCESS = [
  { icon: MessageSquare, t: "Enquire", b: "Tell us about your business and what you want to stock." },
  { icon: ClipboardCheck, t: "Discuss product mix & pricing", b: "We agree the right range, weekly volume and discounts based on weekly invoice value." },
  { icon: FileText, t: "Set supply schedule", b: "Delivery days, cut-off times, weekly invoicing." },
  { icon: Truck, t: "Deliver & review", b: "We deliver fresh, track returns and tune the range." },
];

const FAQS = [
  ["What areas do you cover?", "We primarily cover St Neots, surrounding villages, Huntingdonshire and Cambridgeshire. Deliveries outside these areas are available subject to order value and frequency."],
  ["What types of businesses do you supply?", "Any business that sells food or requires a trusted food supply partner."],
  ["Do you offer flexible order quantities?", "Minimum order values apply, though flexible plans can be tailored to suit your needs."],
  ["How do returns and invoicing work?", "Any items past their sell-by date should be removed from the shelf by the client and kept ready for collection by the SugaSpice delivery driver on your next scheduled delivery. A returns note will be provided and credits applied to the same week's invoice. Returns are monitored weekly and order levels adjusted if needed."],
  ["Can we discuss custom product needs?", "Yes of course — just get in touch using the wholesale enquiry form below."],
  ["How do we get started?", "Please contact us using the wholesale enquiry form below. We'll then be in touch to arrange a visit from one of our team, who will discuss your requirements and leave a selection of sample products for you to try."],
];

export default function Wholesale() {
  const ref = useReveal();
  useSEO({
    title: "Wholesale Bakery & Food Supply — SugaSpice, Cambridgeshire",
    description: "Wholesale fresh bakery and grab-and-go food supply for petrol stations, convenience stores, cafés and retailers across St Neots and Cambridgeshire. Make a wholesale enquiry.",
    image: PACK_BOX,
  });

  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    business_type: "",
    location: "",
    interests: "",
    expected_weekly_volume: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.business_type || !form.location) {
      toast.error("Please fill the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/wholesale/leads", form);
      setSuccess(true);
      toast.success("Enquiry sent. We'll be in touch shortly.");
      setForm({ business_name: "", contact_name: "", email: "", phone: "", business_type: "", location: "", interests: "", expected_weekly_volume: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={ref}>
      {/* HERO */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-24 bg-[#1A1A1A] text-[#F7F5F0] overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 fade-up">
              <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#C8102E]">For business</span>
              <h1 className="mt-5 font-display text-[clamp(2.6rem,7vw,6.5rem)] leading-[0.92] tracking-tight uppercase">
                Wholesale bakery <br /> & fresh grab-and-go <br /> <span className="text-[#C8102E]">food for local businesses.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg text-[#C9C5BE] leading-relaxed">
                SugaSpice supplies fresh, high-quality bakery and grab-and-go food to shops, forecourts, cafés and local retailers across St Neots, Huntingdonshire, Cambridgeshire and surrounding areas.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#enquire" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]" data-testid="wholesale-hero-cta">
                  Request Wholesale Info <ArrowUpRight className="h-4 w-4" />
                </a>
                <a href="#process" className="btn-pill border-2 border-[#F7F5F0]/30 text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1A1A1A]">
                  How it works
                </a>
              </div>
              <p className="mt-10 text-xs tracking-[0.2em] uppercase text-[#A3A3A3]">
                Already trusted by many partners.
              </p>
            </div>
            <div className="lg:col-span-5 fade-up">
              <div className="aspect-square rounded-3xl overflow-hidden">
                <img loading="lazy" decoding="async" src={IMG.wholesale_delivery} alt="Wholesale bakery bread rolls" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["Sale or Return", "Refrigerated Daily Deliveries", "Returns Handled", "Real Margin", "Quality Fresh Products", "Premium Packaging", "Efficient Order System", "Weekly Invoicing"]} theme="light" />

      {/* WHO WE SUPPLY */}
      <section className="py-24 md:py-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <SectionHeading eyebrow="Who we supply" title="Built for everyday business shelves." />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mt-12">
            {SUPPLY_TO.map((s, i) => (
              <div key={s.name} className="fade-up bg-white rounded-3xl p-6 border border-[#D1C9BE]/60 text-center hover:bg-[#1A1A1A] hover:text-[#F7F5F0] transition-colors group" style={{ transitionDelay: `${i * 50}ms` }} data-testid={`wholesale-supply-${i}`}>
                <div className="mx-auto h-12 w-12 rounded-full bg-[#E8E3DB] group-hover:bg-[#C8102E] text-[#1A1A1A] group-hover:text-[#F7F5F0] flex items-center justify-center transition-colors">
                  <s.icon className="h-5 w-5" />
                </div>
                <p className="mt-4 text-sm font-semibold tracking-[0.12em] uppercase">{s.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE SUPPLY */}
      <section className="py-24 md:py-32 bg-[#E8E3DB]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <SectionHeading eyebrow="What we supply" title="A full shelf, ready to sell." intro="Fresh products with strong presentation that lift your bakery and grab-and-go offer — minimal effort, maximum returns." />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6 mt-12">
            {SUPPLY_WHAT.map((p, i) => (
              <div key={p.name} className="fade-up group relative aspect-[4/5] rounded-3xl overflow-hidden" style={{ transitionDelay: `${i * 50}ms` }} data-testid={`wholesale-product-${i}`}>
                <img loading="lazy" decoding="async" src={p.img} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 to-transparent" />
                <h4 className="absolute bottom-5 left-5 font-display text-xl md:text-2xl uppercase tracking-tight text-[#F7F5F0]">{p.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY WORK WITH US */}
      <section className="py-24 md:py-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 fade-up">
              <SectionHeading eyebrow="Why partner with us" title="A smarter, lower-risk way to grow bakery and grab-and-go sales." intro="A flexible partnership model. Returns are tracked and credited transparently through weekly invoicing — so you stock confidently without the waste anxiety." />
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 fade-up">
              {REASONS.map((r) => (
                <div key={r} className="flex items-start gap-3 bg-white rounded-2xl p-5 border border-[#D1C9BE]/60">
                  <CheckCircle2 className="h-5 w-5 mt-1 text-[#C8102E] shrink-0" />
                  <p className="text-sm text-[#1A1A1A] leading-snug">{r}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-24 md:py-32 bg-[#1A1A1A] text-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <SectionHeading eyebrow="How it works" title="From enquiry to delivery." invert />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-14">
            {PROCESS.map((p, i) => (
              <div key={p.t} className="fade-up bg-[#262626] rounded-3xl p-7 relative" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`wholesale-process-${i}`}>
                <span className="font-display text-5xl text-[#C8102E]">0{i + 1}</span>
                <p.icon className="h-7 w-7 mt-6" />
                <h4 className="font-display text-xl md:text-2xl uppercase tracking-tight mt-4">{p.t}</h4>
                <p className="mt-2 text-sm text-[#C9C5BE]">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="enquire" className="py-24 md:py-32 bg-[#E8E3DB]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 fade-up lg:sticky lg:top-32">
              <SectionHeading eyebrow="Wholesale enquiry" title="Let's get your shelves looking serious." intro="Drop the form. We'll come back within 1 business day with a tailored product list, pricing and delivery options." />
            </div>
            <div className="lg:col-span-7">
              {success ? (
                <div className="bg-[#1A1A1A] text-[#F7F5F0] rounded-3xl p-10 fade-up" data-testid="wholesale-success">
                  <CheckCircle2 className="h-10 w-10 text-[#C8102E]" />
                  <h3 className="font-display text-3xl uppercase tracking-tight mt-5">Enquiry received.</h3>
                  <p className="mt-3 text-[#C9C5BE]">We've got your details and we'll be in touch within 1 business day. Excited to talk shop.</p>
                  <button onClick={() => setSuccess(false)} className="btn-pill mt-8 bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]" data-testid="wholesale-success-reset">Submit another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-[#F7F5F0] rounded-3xl p-7 md:p-10 border border-[#D1C9BE]/60 grid grid-cols-1 md:grid-cols-2 gap-5 fade-up" data-testid="wholesale-form">
                  <Field label="Business name *" testId="wf-business">
                    <input required value={form.business_name} onChange={update("business_name")} data-testid="wf-business-name" />
                  </Field>
                  <Field label="Contact name *" testId="wf-contact">
                    <input required value={form.contact_name} onChange={update("contact_name")} data-testid="wf-contact-name" />
                  </Field>
                  <Field label="Email *" testId="wf-email">
                    <input type="email" required value={form.email} onChange={update("email")} data-testid="wf-email" />
                  </Field>
                  <Field label="Phone" testId="wf-phone">
                    <input value={form.phone} onChange={update("phone")} data-testid="wf-phone" />
                  </Field>
                  <Field label="Business type *" testId="wf-type">
                    <select required value={form.business_type} onChange={update("business_type")} data-testid="wf-business-type">
                      <option value="">Select…</option>
                      <option>Petrol station / forecourt</option>
                      <option>Convenience store</option>
                      <option>Independent retailer</option>
                      <option>Café</option>
                      <option>Other food business</option>
                    </select>
                  </Field>
                  <Field label="Location *" testId="wf-location">
                    <input required value={form.location} onChange={update("location")} placeholder="Town / postcode" data-testid="wf-location" />
                  </Field>
                  <Field label="What are you interested in?" testId="wf-interests" full>
                    <input value={form.interests} onChange={update("interests")} placeholder="e.g. pastries, sandwiches, bread" data-testid="wf-interests" />
                  </Field>
                  <Field label="Expected weekly volume" testId="wf-volume" full>
                    <input value={form.expected_weekly_volume} onChange={update("expected_weekly_volume")} placeholder="If known" data-testid="wf-volume" />
                  </Field>
                  <Field label="Message" testId="wf-message" full>
                    <textarea rows={4} value={form.message} onChange={update("message")} data-testid="wf-message" />
                  </Field>
                  <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                    <p className="text-xs text-[#5C5C5C] max-w-sm leading-relaxed" data-testid="wholesale-privacy-note">We'll use your information to respond to your enquiry. See our <Link to="/privacy" className="text-[#C8102E] underline">Privacy Policy</Link> for details.</p>
                    <button type="submit" disabled={submitting} className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] disabled:opacity-60" data-testid="wf-submit">
                      {submitting ? "Sending…" : "Send Enquiry"} <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-16">
          <SectionHeading eyebrow="FAQ" title="Wholesale questions, answered." />
          <div className="mt-12 divide-y divide-[#D1C9BE]/70 border-y border-[#D1C9BE]/70" data-testid="wholesale-faq">
            {FAQS.map(([q, a], i) => (
              <details key={i} className="group py-6 fade-up" style={{ transitionDelay: `${i * 30}ms` }}>
                <summary className="cursor-pointer list-none flex items-center justify-between gap-6">
                  <span className="font-display text-lg md:text-2xl uppercase tracking-tight">{q}</span>
                  <span className="text-2xl text-[#C8102E] transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-[#3a3a3a] leading-relaxed max-w-3xl">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, full = false, testId }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`} data-testid={testId}>
      <span className="text-[11px] tracking-[0.18em] uppercase text-[#5C5C5C] font-semibold">{label}</span>
      <span className="[&_input]:bg-white [&_textarea]:bg-white [&_select]:bg-white [&_input]:border [&_textarea]:border [&_select]:border [&_input]:border-[#D1C9BE] [&_textarea]:border-[#D1C9BE] [&_select]:border-[#D1C9BE] [&_input]:rounded-2xl [&_textarea]:rounded-2xl [&_select]:rounded-2xl [&_input]:px-5 [&_textarea]:px-5 [&_select]:px-5 [&_input]:py-3.5 [&_textarea]:py-3.5 [&_select]:py-3.5 [&_input]:text-sm [&_textarea]:text-sm [&_select]:text-sm [&_input]:w-full [&_textarea]:w-full [&_select]:w-full focus-within:[&_input]:border-[#C8102E] focus-within:[&_textarea]:border-[#C8102E] focus-within:[&_select]:border-[#C8102E] [&_input]:outline-none [&_textarea]:outline-none [&_select]:outline-none">
        {children}
      </span>
    </label>
  );
}
